import { toast } from "react-toastify"

import { REQ } from "constants/api/requests"

import useApi, { isAuthorityUnavailableError } from "util/api"

import { FeeImportResult, FeeRateUpdateResult, PaginatedProviders, ProviderGeoRestriction, ProviderSearchParams } from "./type"

class ProvidersService {
  async getProviders(params: ProviderSearchParams): Promise<PaginatedProviders | undefined> {
    try {
      const { data } = await useApi.get<{
        success: boolean
        response: PaginatedProviders
      }>(REQ.ADMIN_DASHBOARD.PROVIDERS.GET, { params })

      return data?.response
    } catch {
      toast.error("Failed to fetch providers")
    }
  }

  async toggleEnabled(id: string, enabled: boolean): Promise<boolean> {
    try {
      await useApi.patch(REQ.ADMIN_DASHBOARD.PROVIDERS.TOGGLE_ENABLED(id), { enabled })
      return true
    } catch {
      toast.error("Failed to update provider status")
      return false
    }
  }

  // D02/F18: returns the authoritative result (`previous_fee_rate` / `nux_synced` / optional
  // `warning`) so the caller can surface them, or `null` on failure. Fail-closed: a 503 (NUX
  // unavailable) is retryable; a 400 carries a reason (e.g. provider unknown to the service).
  async updateFeeRate(id: string, feeRate: number): Promise<FeeRateUpdateResult | null> {
    try {
      const { data } = await useApi.patch<{ success: boolean; response: FeeRateUpdateResult }>(
        REQ.ADMIN_DASHBOARD.PROVIDERS.UPDATE_FEE_RATE(id),
        { feeRate }
      )
      return data?.response ?? { previous_fee_rate: null, nux_synced: false }
    } catch (err: any) {
      if (isAuthorityUnavailableError(err)) {
        toast.error("Game provider service is temporarily unavailable — please try again in a moment.")
      } else {
        toast.error(err?.data?.message || "Failed to update fee rate")
      }
      return null
    }
  }

  async exportFeesCsv(): Promise<boolean> {
    try {
      const { data } = await useApi.get<Blob>(REQ.ADMIN_DASHBOARD.PROVIDERS.FEE_EXPORT, {
        responseType: "blob"
      })

      const blob = new Blob([data], { type: "text/csv;charset=utf-8;" })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", "provider-fees.csv")
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      return true
    } catch {
      toast.error("Failed to export provider fees")
      return false
    }
  }

  async importFeesCsv(file: File): Promise<boolean> {
    try {
      const formData = new FormData()
      formData.append("file", file)

      const { data } = await useApi.post<{
        success: boolean
        response: FeeImportResult
      }>(REQ.ADMIN_DASHBOARD.PROVIDERS.FEE_IMPORT, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      })

      if (!data?.success) {
        toast.error("Failed to import provider fees")
        return false
      }

      toast.success(`Updated fees for ${data.response.updatedCount} providers`)
      // D02/F18: rows NUX rejected on MS tenants are reported, not silently dropped — surface each.
      const failures = data.response.failures ?? []
      failures.forEach((f) => toast.warn(`${f.identifier}: ${f.error}`))
      return true
    } catch (err: any) {
      if (isAuthorityUnavailableError(err)) {
        toast.error("Game provider service is temporarily unavailable — please try again in a moment.")
      } else {
        toast.error(err?.data?.message || "Failed to import provider fees")
      }
      return false
    }
  }

  async getRestrictions(): Promise<ProviderGeoRestriction[]> {
    try {
      const { data } = await useApi.get<{ success: boolean; response: ProviderGeoRestriction[] }>(
        REQ.ADMIN_DASHBOARD.PROVIDERS.RESTRICTIONS.LIST
      )
      return data?.response ?? []
    } catch {
      toast.error("Failed to fetch provider restrictions")
      return []
    }
  }

  async upsertRestriction(
    id: string,
    payload: { blocked_countries: string[] }
  ): Promise<boolean> {
    try {
      await useApi.put(REQ.ADMIN_DASHBOARD.PROVIDERS.RESTRICTIONS.UPSERT(id), payload)
      return true
    } catch {
      toast.error("Failed to save provider restriction")
      return false
    }
  }

  async deleteRestriction(id: string): Promise<boolean> {
    try {
      await useApi.delete(REQ.ADMIN_DASHBOARD.PROVIDERS.RESTRICTIONS.DELETE(id))
      return true
    } catch {
      toast.error("Failed to delete provider restriction")
      return false
    }
  }
}

export const providersService = new ProvidersService()

